import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getAuthService, getFirestoreService } from "../firebase/services";
import { Loader2 } from "lucide-react";

const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const location = useLocation();

  useEffect(() => {
    let unsub = null;
    let cancelled = false;

    const init = async () => {
      const auth = await getAuthService();
      const db = await getFirestoreService();
      const { onAuthStateChanged } = await import("firebase/auth");
      const { doc, getDoc, updateDoc } = await import("firebase/firestore");
      if (cancelled) return;

      unsub = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          setUser(firebaseUser);
          try {
            const userDocRef = doc(db, "users", firebaseUser.uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              const data = userDoc.data();
              let currentStatus = data.status || "active";

              if (currentStatus === "active" && data.planExpiresAt) {
                const expiryDate = data.planExpiresAt.toDate ? data.planExpiresAt.toDate() : new Date(data.planExpiresAt);
                if (expiryDate < new Date()) {
                  currentStatus = "expired";
                  await updateDoc(userDocRef, { status: "expired" });
                }
              }

              setStatus(currentStatus);
            } else {
              setStatus("active");
            }
          } catch (error) {
            console.error("Error fetching user status:", error);
            setStatus("active");
          }
        } else {
          setUser(null);
          setStatus(null);
        }
        setLoading(false);
      });
    };

    init();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-[#131314]">
        <Loader2 className="animate-spin text-[#F4B400] mb-4" size={48} />
        <p className="font-inter font-bold uppercase tracking-widest text-white/60">
          Loading...
        </p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (status === "pending_payment") {
    return <Navigate to="/holding" replace />;
  }

  if (status === "expired") {
    return <Navigate to="/expired" replace />;
  }

  if (status === "suspended") {
    return <Navigate to="/suspended" replace />;
  }

  return children;
};

export default ProtectedRoute;
